import { TGoal } from '../HealthGoalSelection/HealthGoalSelection';

export const SELECTION_TYPE = {
	PREGNANT_GETTING_FIT: 'PREGNANT_GETTING_FIT',
	PREGNANT_GETTING_FIT_SELECTED: 'PREGNANT_GETTING_FIT_SELECTED',
	PREGNANT_HEALTHIER: 'PREGNANT_HEALTHIER',
	PREGNANT_HEALTHIER_SELECTED: 'PREGNANT_HEALTHIER_SELECTED',
	PREGNANT_DISABLED: 'PREGNANT_DISABLED',
	PREGNANT_DISABLED_SELECTED: 'PREGNANT_DISABLED_SELECTED',
	UNDERWEIGHT_GETTING_FIT: 'UNDERWEIGHT_GETTING_FIT',
	UNDERWEIGHT_GETTING_FIT_SELECTED: 'UNDERWEIGHT_GETTING_FIT_SELECTED',
	UNDERWEIGHT_HEALTHIER: 'UNDERWEIGHT_HEALTHIER',
	UNDERWEIGHT_HEALTHIER_SELECTED: 'UNDERWEIGHT_HEALTHIER_SELECTED',
	UNDERWEIGHT_DISABLED: 'UNDERWEIGHT_DISABLED',
	UNDERWEIGHT_DISABLED_SELECTED: 'UNDERWEIGHT_DISABLED_SELECTED',
	NORMAL_SELECTED: 'NORMAL_SELECTED',
	NORMAL_SELECTED_RECOMMENDED: 'NORMAL_SELECTED_RECOMMENDED',
	NORMAL_INACTIVE: 'NORMAL_INACTIVE',
	NORMAL_INACTIVE_RECOMMENDED: 'NORMAL_INACTIVE_RECOMMENDED',
	OVERWEIGHT_SELECTED: 'OVERWEIGHT_SELECTED',
	OVERWEIGHT_INACTIVE: 'OVERWEIGHT_INACTIVE',
	SELECTED: 'SELECTED',
	INACTIVE: 'INACTIVE',
} as const;

export type SelectionTypeType = keyof typeof SELECTION_TYPE;

export type getGoalSelectionTypeOptionsType = {
	isPregnant?: boolean;
	bmiValue: number;
	goal: {
		name: TGoal;
	};
	selectedGoal?: TGoal;
};

export type IdentifyGoalType = {
	isRecommended: boolean;
	isSelected: boolean;
	isDisabled: boolean;
};

export const isUnderWeightByBMI = (bmiValue: number) => Boolean(bmiValue <= 18.5);
export const isNormalWeightByBMI = (bmiValue: number) => Boolean(bmiValue > 18.5 && bmiValue <= 20);
export const isOverWeightByBMI = (bmiValue: number) => Boolean(bmiValue >= 30);

export function identifyGoal(type: SelectionTypeType): IdentifyGoalType {
	const map = {
		PREGNANT_GETTING_FIT: () => ({
			isRecommended: true,
			isSelected: false,
			isDisabled: false,
		}),
		PREGNANT_GETTING_FIT_SELECTED: () => ({
			isRecommended: true,
			isSelected: true,
			isDisabled: false,
		}),
		PREGNANT_HEALTHIER: () => ({
			isRecommended: false,
			isSelected: false,
			isDisabled: false,
		}),
		PREGNANT_HEALTHIER_SELECTED: () => ({
			isRecommended: false,
			isSelected: true,
			isDisabled: false,
		}),
		PREGNANT_DISABLED: () => ({
			isRecommended: false,
			isSelected: false,
			isDisabled: true,
		}),
		PREGNANT_DISABLED_SELECTED: () => ({
			isRecommended: false,
			isSelected: true,
			isDisabled: true,
		}),
		UNDERWEIGHT_GETTING_FIT: () => ({
			isRecommended: true,
			isSelected: false,
			isDisabled: false,
		}),
		UNDERWEIGHT_GETTING_FIT_SELECTED: () => ({
			isRecommended: true,
			isSelected: true,
			isDisabled: false,
		}),
		UNDERWEIGHT_HEALTHIER: () => ({
			isRecommended: false,
			isSelected: false,
			isDisabled: false,
		}),
		UNDERWEIGHT_HEALTHIER_SELECTED: () => ({
			isRecommended: false,
			isSelected: true,
			isDisabled: false,
		}),
		UNDERWEIGHT_DISABLED: () => ({
			isRecommended: false,
			isSelected: false,
			isDisabled: true,
		}),
		UNDERWEIGHT_DISABLED_SELECTED: () => ({
			isRecommended: false,
			isSelected: true,
			isDisabled: true,
		}),
		NORMAL_SELECTED: () => ({
			isRecommended: false,
			isSelected: true,
			isDisabled: false,
		}),
		NORMAL_SELECTED_RECOMMENDED: () => ({
			isRecommended: true,
			isSelected: true,
			isDisabled: false,
		}),
		NORMAL_INACTIVE: () => ({
			isRecommended: false,
			isSelected: false,
			isDisabled: false,
		}),
		NORMAL_INACTIVE_RECOMMENDED: () => ({
			isRecommended: true,
			isSelected: false,
			isDisabled: false,
		}),
		OVERWEIGHT_SELECTED: () => ({
			isRecommended: true,
			isSelected: true,
			isDisabled: false,
		}),
		OVERWEIGHT_INACTIVE: () => ({
			isRecommended: true,
			isSelected: false,
			isDisabled: false,
		}),
		SELECTED: () => ({
			isRecommended: false,
			isSelected: true,
			isDisabled: false,
		}),
		INACTIVE: () => ({
			isRecommended: false,
			isSelected: false,
			isDisabled: false,
		}),
	};
	return map[type]();
}

function getGoalSelectionType({
	isPregnant = false,
	bmiValue,
	goal,
	selectedGoal,
}: getGoalSelectionTypeOptionsType): SelectionTypeType {
	const { name } = goal;

	const isGettingFit: boolean = Boolean(name === 'running');
	const isLoseWeight: boolean = Boolean(name === 'fruits');
	const isGettingHealthier: boolean = Boolean(name === 'healthier');

	const isUnderWeight: boolean = isUnderWeightByBMI(bmiValue);
	const isNormalWeight: boolean = isNormalWeightByBMI(bmiValue);
	const isOverWeight: boolean = isOverWeightByBMI(bmiValue);

	const isSelected: boolean = Boolean(selectedGoal === name);

	if (isPregnant) {
		if (isGettingFit) {
			return isSelected ? SELECTION_TYPE.PREGNANT_GETTING_FIT_SELECTED : SELECTION_TYPE.PREGNANT_GETTING_FIT;
		} else if (isGettingHealthier) {
			return isSelected ? SELECTION_TYPE.PREGNANT_HEALTHIER_SELECTED : SELECTION_TYPE.PREGNANT_HEALTHIER;
		} else if (isLoseWeight) {
			return isSelected ? SELECTION_TYPE.PREGNANT_DISABLED_SELECTED : SELECTION_TYPE.PREGNANT_DISABLED;
		}
	}

	if (isUnderWeight) {
		if (isGettingFit) {
			return isSelected
				? SELECTION_TYPE.UNDERWEIGHT_GETTING_FIT_SELECTED
				: SELECTION_TYPE.UNDERWEIGHT_GETTING_FIT;
		} else if (isGettingHealthier) {
			return isSelected ? SELECTION_TYPE.UNDERWEIGHT_HEALTHIER_SELECTED : SELECTION_TYPE.UNDERWEIGHT_HEALTHIER;
		} else if (isLoseWeight) {
			return isSelected ? SELECTION_TYPE.UNDERWEIGHT_DISABLED_SELECTED : SELECTION_TYPE.UNDERWEIGHT_DISABLED;
		}
		return SELECTION_TYPE.UNDERWEIGHT_DISABLED;
	}

	if (isNormalWeight && isSelected) {
		return isGettingFit ? SELECTION_TYPE.NORMAL_SELECTED_RECOMMENDED : SELECTION_TYPE.NORMAL_SELECTED;
	}

	if (isNormalWeight) {
		return isGettingFit ? SELECTION_TYPE.NORMAL_INACTIVE_RECOMMENDED : SELECTION_TYPE.NORMAL_INACTIVE;
	}

	if (isOverWeight && isLoseWeight) {
		return !selectedGoal || isSelected ? SELECTION_TYPE.OVERWEIGHT_SELECTED : SELECTION_TYPE.OVERWEIGHT_INACTIVE;
	}

	if (isSelected) {
		return SELECTION_TYPE.SELECTED;
	}

	return SELECTION_TYPE.INACTIVE;
}

export default getGoalSelectionType;
